import React, { useEffect, useRef } from "react";

type AnimatedAuroraProps = {
  className?: string;
  height?: number;
  blur?: number;
};

type AuroraBlob = {
  baseX: number;
  baseY: number;
  radius: number;
  color: string;
  phaseX: number;
  phaseY: number;
  speedX: number;
  speedY: number;
};

export const AnimatedAurora: React.FC<AnimatedAuroraProps> = ({ className, height = 480, blur = 60 }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let isRunning = true;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const parent = canvas.parentElement;
      const width = parent ? parent.clientWidth : window.innerWidth;
      const heightPx = parent ? Math.max(parent.clientHeight, height) : height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(heightPx * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${heightPx}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

    const palette = [
      "rgba(29, 78, 216, 0.50)",   // blue-700
      "rgba(37, 99, 235, 0.40)",   // blue-600
      "rgba(59, 130, 246, 0.32)",  // blue-500
      "rgba(14, 165, 233, 0.28)",  // sky-500
      "rgba(56, 189, 248, 0.24)",  // sky-400
    ];

    const createBlobs = (count: number, width: number, heightPx: number): AuroraBlob[] => {
      const blobs: AuroraBlob[] = [];
      for (let i = 0; i < count; i++) {
        blobs.push({
          baseX: randomBetween(width * 0.1, width * 0.9),
          baseY: randomBetween(heightPx * 0.1, heightPx * 0.9),
          radius: randomBetween(Math.min(width, heightPx) * 0.25, Math.min(width, heightPx) * 0.55),
          color: palette[i % palette.length],
          phaseX: randomBetween(0, Math.PI * 2),
          phaseY: randomBetween(0, Math.PI * 2),
          speedX: randomBetween(0.15, 0.35),
          speedY: randomBetween(0.12, 0.32),
        });
      }
      return blobs;
    };

    let blobs = createBlobs(5, canvas.width / dpr, canvas.height / dpr);

    const draw = (timeMs: number) => {
      if (!isRunning) return;

      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      ctx.clearRect(0, 0, w, h);

      ctx.globalCompositeOperation = "lighter";

      for (let i = 0; i < blobs.length; i++) {
        const b = blobs[i];
        const t = timeMs / 1000;
        const x = b.baseX + Math.sin(t * b.speedX + b.phaseX) * (w * 0.18);
        const y = b.baseY + Math.cos(t * b.speedY + b.phaseY) * (h * 0.18);

        const gradient = ctx.createRadialGradient(x, y, 0, x, y, b.radius);
        gradient.addColorStop(0, b.color);
        gradient.addColorStop(1, "rgba(0,0,0,0)");

        ctx.fillStyle = gradient;
        ctx.filter = `blur(${blur}px)`;
        ctx.beginPath();
        ctx.arc(x, y, b.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.filter = "none";
      ctx.globalCompositeOperation = "source-over";

      const vignette = ctx.createLinearGradient(0, 0, 0, h);
      vignette.addColorStop(0, "rgba(30, 58, 138, 0.10)");
      vignette.addColorStop(1, "rgba(30, 58, 138, 0)");
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, w, h);

      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);

    const onVisibility = () => {
      const visible = document.visibilityState === "visible";
      if (!visible && animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      } else if (visible && !animationRef.current) {
        animationRef.current = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onResize = () => {
      const prevCount = blobs.length;
      resize();
      blobs = createBlobs(prevCount, canvas.width / dpr, canvas.height / dpr);
    };
    window.addEventListener("resize", onResize);

    return () => {
      isRunning = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [height, blur]);

  return (
    <div className={className} aria-hidden="true">
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height }} />
    </div>
  );
};

export default AnimatedAurora;


